import { useState } from 'react';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { fetchSortableList } from '../services/listService';
import { SortDirection } from '../models/DataTable';

interface UseSortableListOptions {
    entity: string;
    filter: Record<string, unknown>;
    take: number;
    defaultSortBy?: string;
    enabled?: boolean;
}

const useSortableList = <T>({ entity, filter, take, defaultSortBy = '', enabled = true }: UseSortableListOptions) => {
    const [sortBy, setSortBy] = useState(defaultSortBy);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [searchTrigger, setSearchTrigger] = useState(0);

    const { data, isFetching, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
        queryKey: [entity, filter, sortBy, sortDirection, searchTrigger],
        queryFn: ({ pageParam }) => fetchSortableList<T>(entity, filter, pageParam, take, sortBy, sortDirection),
        initialPageParam: 0,
        staleTime: 0,
        enabled,
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
            const fetched = allPages.flatMap(p => p.data).length;
            return fetched < lastPage.totalCount ? fetched : undefined;
        }
    });

    const onSort = (key: string) => {
        const newDirection = sortBy === key && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortBy(key);
        setSortDirection(newDirection);
    };

    const triggerSearch = () => setSearchTrigger(t => t + 1);

    const resetSort = () => {
        setSortBy(defaultSortBy);
        setSortDirection('asc');
        setSearchTrigger(t => t + 1);
    };

    const allData = data?.pages.flatMap(p => p.data) ?? [];
    const totalCount = data?.pages[0]?.totalCount ?? 0;

    return {
        data: allData,
        totalCount,
        isLoading,
        isFetching,
        sortBy,
        sortDirection,
        onSort,
        resetSort,
        triggerSearch,
        fetchNextPage,
        hasNextPage
    };
};

export default useSortableList;
